import React from "react";
import { Link } from "react-router-dom";
import { shareOrigin } from "../lib/shareOrigin";
import InlineShareButton from "../components/InlineShareButton";
import GuideVideos from "../components/GuideVideos";
import {
  MapTrifold, Star, Bell, FireSimple, VideoCamera, Users, MapPin,
  DeviceMobile, ShareNetwork, FileText, Warning, CompassRose, Heart,
  PencilSimple, Broadcast, ChartLine, ArrowRight, Camera,
} from "@phosphor-icons/react";

function Section({ icon: Icon, num, title, children }) {
  return (
    <section className="border border-tac-border bg-tac-surface/40 rounded-sm mb-5">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-tac-border">
        <span className="font-mono-tac tabular text-tac-primary text-sm font-bold">{String(num).padStart(2, "0")}</span>
        <Icon size={18} weight="fill" className="text-tac-primary" />
        <h2 className="font-display font-black text-lg tracking-tight text-tac-text">{title}</h2>
      </div>
      <div className="px-5 py-4 space-y-3">{children}</div>
    </section>
  );
}

function Step({ n, children }) {
  return (
    <div className="flex items-start gap-3">
      <span className="shrink-0 w-5 h-5 mt-0.5 flex items-center justify-center rounded-full bg-tac-primary/15 border border-tac-primary/50 text-tac-primary text-[11px] font-bold">
        {n}
      </span>
      <p className="text-[15px] leading-relaxed text-tac-text">{children}</p>
    </div>
  );
}

function Tip({ children }) {
  return (
    <div className="border-l-2 border-tac-warning/70 bg-tac-warning/5 pl-3 py-2 text-[14px] text-tac-dim">
      {children}
    </div>
  );
}

const K = ({ children }) => (
  <span className="px-1.5 py-0.5 mx-0.5 text-[12px] font-mono-tac border border-tac-border bg-tac-bg rounded-sm text-tac-primary">
    {children}
  </span>
);

export default function Guide() {
  const caption = [
    "📖 How to Use Firewatch — the field manual for the Okanogan Highlands Firewatch wildfire map.",
    "Ten minutes here and you'll get more out of every fire season: reading the map, saved location alerts, cameras, scanners and more.",
    `Read the guide: ${shareOrigin()}/guide`,
  ].join("\n");
  return (
    <div className="min-h-screen bg-tac-bg text-tac-text px-4 py-10" data-testid="guide-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="text-[12px] font-bold uppercase tracking-[0.2em] text-tac-primary">
            Field Manual
          </div>
          <InlineShareButton
            testid="guide-share-btn"
            url={`${shareOrigin()}/guide`}
            title="How to Use Firewatch"
            text={caption}
            caption={caption}
          />
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-3">
          How to Use Firewatch
        </h1>
        <p className="text-[15px] text-tac-dim leading-relaxed mb-8 max-w-xl">
          Ten minutes here and you'll get more out of every fire season. Each section is a
          short set of steps — follow along on your own screen.
        </p>

        <GuideVideos />

        <Section icon={MapTrifold} num={1} title="Reading the Live Map">
          <Step n={1}>Pick your area with the <K>County</K> selector in the top bar — individual counties, all of Washington, or the BC border zone.</Step>
          <Step n={2}>Flame icons are active dispatches; red outlines are official mapped fire perimeters. Colored dots are satellite heat detections — bright orange is fresh (under 12 h), amber is 12–24 h, grey is older.</Step>
          <Step n={3}>Shaded zones are evacuation levels: green = Level 1 (Ready), orange = Level 2 (Set), red = Level 3 (Go now). Tap any zone or its zone-number pill (e.g. A1330) for details, including road closures inside it.</Step>
          <Step n={4}>Use the <K>Layers</K> menu (top-left of the map) to toggle hotspots, aircraft, cameras, lightning, smoke forecast, <K>Live Wind</K> arrows, <K>Rain Radar</K>, and more. The <K>Legend</K> button decodes every symbol.</Step>
          <Step n={5}>Tap the <K>🔍</K> next to + Info Pin to search any address or town — the map flies there and drops a temporary marker. The same search lives on every map, including the pin-drop menus.</Step>
          <Step n={6}><K>Refresh</K> in the top bar pulls the latest from every source; the timestamp next to it shows data age.</Step>
          <Step n={7}>The <K>Trending</K> chip (next to the status filters) shows only fires that grew in the last 24 hours — the ones worth watching today.</Step>
        </Section>

        <Section icon={Camera} num={2} title="Map Tools: Screenshot & Measure">
          <Step n={1}>The <K>📷</K> camera button (top-left, next to search) copies a screenshot of your exact map view to the clipboard — paste it straight into a Facebook post with Ctrl+V. Every capture is stamped with the Firewatch watermark and timestamp.</Step>
          <Step n={2}>The <K>📏</K> ruler button measures in miles: first click starts the tape, second click locks the distance, keep clicking to extend it leg by leg. <K>Esc</K> or the ruler button clears it.</Step>
          <Step n={3}>Turn on the Lightning layer and click any strike to pin its tooltip — pin several at once, then hit the camera button to capture them all in one shot.</Step>
          <Tip>
            Measure + screenshot is a powerful combo: tape the distance from a fire to town,
            capture it, and the shot is ready to post — watermark, mileage labels and all.
          </Tip>
        </Section>

        <Section icon={Star} num={3} title="Save Your Home Location">
          <Step n={1}>Tap the <K>★</K> button in the top bar, then <K>Add Location</K>.</Step>
          <Step n={2}>Search an address or town — or paste coordinates directly. Both <K>48.9059, -119.0113</K> and <K>48°54'21.2"N 119°00'40.7"W</K> work.</Step>
          <Step n={3}>Pick an alert radius (5–50 mi) and save. Your pin appears on the map with its radius ring.</Step>
          <Step n={4}>To remove a pin, tap it on the map and hit <K>Remove pin</K>, or manage the list from the ★ menu.</Step>
          <Tip>
            Skip "Use my location" if you're on satellite internet or a VPN — browsers locate
            your provider, not your house, sometimes by hundreds of miles. Pasting your exact
            coordinates is always right.
          </Tip>
        </Section>

        <Section icon={FireSimple} num={4} title="Follow a Fire">
          <Step n={1}>Open any incident and tap <K>Follow</K>.</Step>
          <Step n={2}>The Following panel on the Live Map lists your fires with live distance and direction from your saved location — measured to the <em>nearest edge of the fire perimeter</em>, not the center.</Step>
          <Step n={3}>A dashed line on the map connects your pin to the fire's edge, labeled with mileage and when the perimeter was last mapped.</Step>
          <Step n={4}>Tap <K>✕</K> in the panel to unfollow, or the crosshair to fly the map to the fire.</Step>
        </Section>

        <Section icon={Bell} num={5} title="Turn On Push Alerts">
          <Step n={1}>Tap <K>Enable Alerts</K> when prompted (or from the ★ saved-locations panel) and allow notifications.</Step>
          <Step n={2}>You'll be warned about: new evacuation levels near you, fire activity inside your saved-location radius, rapid growth (fire doubles within 24 h), a followed fire's edge moving a mile-plus closer to home, two large fires converging toward a complex, and lightning clusters — 3+ strikes near a saved location within 30 minutes.</Step>
          <Step n={3}>A morning briefing arrives around 8 AM and an evening recap around 8 PM during active periods.</Step>
        </Section>

        <Section icon={FileText} num={6} title="Incident Pages & AI Summary">
          <Step n={1}>Tap any fire for the full picture: operational status, mapped acres vs. reported acres, weather at the fire, spread direction analysis, nearby cameras, and scanner audio.</Step>
          <Step n={2}>The <K>Summary</K> button opens a plain-language AI-written briefing in a new window — size, containment, evacuations, closures — with its own Share button.</Step>
          <Step n={3}>Use "Is your place in the line of fire?" to drop your address on the incident map and see distance, bearing, and whether the spread points your way.</Step>
          <Step n={4}>On the incident map, toggle <K>Older hotspots</K> and <K>Evac zones</K> (top-right chips) to declutter the view.</Step>
        </Section>

        <Section icon={Users} num={7} title="Field Updates — Community Reports">
          <Step n={1}>Log in (or <Link className="text-tac-primary hover:underline" to="/signup">join</Link>) to post observations to any incident's operations log. Write in paragraphs — Enter starts a new one.</Step>
          <Step n={2}>Verified reporters can attach up to 4 photos; shots with GPS data drop as pins on the map automatically.</Step>
          <Step n={3}><PencilSimple size={13} className="inline text-tac-dim" /> Edit fixes your own post (an "edited" tag appears). <Heart size={13} className="inline text-tac-danger" /> marks a report as read — everyone can see who's seen it.</Step>
          <Step n={4}>Want the verified badge? Apply from your profile — local reporters, ham operators, and fire-adjacent folks welcome.</Step>
          <Step n={5}>Verified reporters and staff can drop <K>Info Pins</K> on the map — shelters, closures, warnings — choosing an icon (Info, Fire, Evac, Caution, Road Closed, Shelter) plus up to 2 photos and a link.</Step>
        </Section>

        <Section icon={VideoCamera} num={8} title="Cameras, Scanner, History">
          <Step n={1}><K>Cameras</K> shows the WA DNR / Pano wildfire camera network — nearest cameras also appear on each incident page.</Step>
          <Step n={2}>Live Scanner embeds Broadcastify dispatch audio so you can listen to fire traffic.</Step>
          <Step n={3}><K>History</K> holds the incident archive with a heatmap of past seasons.</Step>
        </Section>

        <Section icon={DeviceMobile} num={9} title="Install It Like an App">
          <Step n={1}>Android / Chrome: open the site, tap the ⋮ menu → <K>Add to Home screen</K> → <K>Install</K>.</Step>
          <Step n={2}>iPhone / Safari: tap the share square → <K>Add to Home Screen</K> → <K>Add</K>.</Step>
          <Step n={3}>Windows / Chrome or Edge: click the <K>install icon</K> at the right end of the address bar (small monitor with a down arrow) → <K>Install</K>. Firewatch gets its own window plus a Start menu and desktop icon.</Step>
          <Step n={4}>It launches full-screen, gets push alerts, and updates itself. No app store needed.</Step>
        </Section>

        <Section icon={ShareNetwork} num={10} title="Sharing">
          <Step n={1}>Every incident, and the site itself, has a <K>Share</K> button — links unfurl with a live map preview card in texts and social posts. The top-bar Share menu can also post straight to Facebook with a pre-written caption.</Step>
          <Step n={2}>The NWCC and NIFC Morning Briefs (More tab) have their own share buttons — the copied caption includes the day's stats, and the link opens the full report in a large pop-out right on the app.</Step>
          <Step n={3}>From your ★ saved locations, <K>Share My Status</K> makes a daily check-in card — nearest fire, distance from your place, and a QR code — so friends and family know you're okay with one post.</Step>
          <Step n={4}>Sharing during an active fire is genuinely useful: neighbors without the app see current acreage and evacuation levels at a glance.</Step>
        </Section>

        <div className="flex flex-wrap gap-3 mt-8">
          <Link
            to="/"
            data-testid="guide-to-map"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-tac-primary text-black font-bold uppercase tracking-wider text-[12px] rounded-sm hover:opacity-90 transition-opacity"
          >
            Open the Live Map <ArrowRight size={14} weight="bold" />
          </Link>
          <Link
            to="/about"
            data-testid="guide-to-about"
            className="inline-flex items-center gap-2 px-4 py-2.5 border border-tac-border text-tac-text font-bold uppercase tracking-wider text-[12px] rounded-sm hover:border-tac-borderBright transition-colors"
          >
            Full feature list
          </Link>
        </div>
      </div>
    </div>
  );
}
